import React, { useState, useEffect, useMemo } from 'react';
import Swal from 'sweetalert2';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { 
  FaEye, 
  FaTrashAlt, 
  FaTimes, 
  FaSearch, 
  FaChevronDown, 
  FaChevronUp, 
  FaFileExport,
  FaFilePdf,
  FaGraduationCap,
  FaUserMd,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaEnvelope,
  FaPhoneAlt,
  FaIdCard,
  FaAngleDoubleRight,
  FaCheck,
  FaBan,
  FaUserCheck
} from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';
import './FacultyRegistrationForm.css';
import axiosInstance from '../../api/axiosInstance';
import Pagination from '../Pagination/Pagination';

/* =========================================================
   1. VIEW MODAL (NO ACTION BUTTONS INSIDE - ONLY DETAILS)
========================================================= */

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

const FacultyViewModal = ({ faculty, onClose }) => {
  if (!faculty) return null;
  const formatDate = (d) => d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : 'N/A';
  const cvUrl = `${API_BASE_URL}/uploads/cv/${faculty.cvFile}`;

  return (
    <div className="faculty-admin-modal-overlay" onClick={onClose}>
      <div className="faculty-admin-modal-content" onClick={(e) => e.stopPropagation()}>
        
        {/* Header */}
        <div className="faculty-admin-modal-header">
          <div className="faculty-admin-modal-header-left">
            <span className="faculty-admin-modal-kicker">Candidate Profile</span>
            <h2>{faculty.fullName}</h2>
          </div>
          <div className="faculty-admin-modal-header-right">
            <span className={`faculty-admin-status-badge ${faculty.status.toLowerCase()}`}>
              {faculty.status}
            </span>
            <button className="faculty-admin-modal-close-btn" onClick={onClose} aria-label="Close">
              <FaTimes />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="faculty-admin-modal-body">
          <div className="faculty-admin-modal-hero-strip">
            <div>
              <span className="faculty-strip-label">Qualification & Speciality:</span>
              <strong>{faculty.qualification} – {faculty.speciality}</strong>
            </div>
            {faculty.cvFile && (
              <a 
                href={cvUrl} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="faculty-admin-modal-cv-btn"
                title="Download / Open CV File"
              >
                <FaFilePdf /> View CV File
              </a>
            )}
          </div>

          <div className="faculty-admin-modal-grid">
            <div className="faculty-admin-modal-card">
              <div className="faculty-admin-card-icon"><FaUserMd /></div>
              <div className="faculty-admin-card-info">
                <label>Current Designation</label>
                <span>{faculty.currentDesignation}</span>
              </div>
            </div>

            <div className="faculty-admin-modal-card">
              <div className="faculty-admin-card-icon"><FaGraduationCap /></div>
              <div className="faculty-admin-card-info">
                <label>Teaching Experience</label>
                <span>{faculty.teachingExperience}</span>
              </div>
            </div>

            <div className="faculty-admin-modal-card">
              <div className="faculty-admin-card-icon"><FaPhoneAlt /></div>
              <div className="faculty-admin-card-info">
                <label>Contact Phone</label>
                <span>{faculty.mobileNumber}</span>
              </div>
            </div>

            <div className="faculty-admin-modal-card">
              <div className="faculty-admin-card-icon"><FaEnvelope /></div>
              <div className="faculty-admin-card-info">
                <label>Email Address</label>
                <span className="text-break">{faculty.email}</span>
              </div>
            </div>

            <div className="faculty-admin-modal-card">
              <div className="faculty-admin-card-icon"><FaMapMarkerAlt /></div>
              <div className="faculty-admin-card-info">
                <label>Current City & State</label>
                <span>{faculty.currentCityState}</span>
              </div>
            </div>

            <div className="faculty-admin-modal-card">
              <div className="faculty-admin-card-icon"><FaMapMarkerAlt /></div>
              <div className="faculty-admin-card-info">
                <label>Preferred Location</label>
                <span>{faculty.preferredLocation}</span>
              </div>
            </div>

            <div className="faculty-admin-modal-card">
              <div className="faculty-admin-card-icon"><FaCalendarAlt /></div>
              <div className="faculty-admin-card-info">
                <label>Joining Availability</label>
                <span>{faculty.joiningAvailability}</span>
              </div>
            </div>

            <div className="faculty-admin-modal-card">
              <div className="faculty-admin-card-icon"><FaIdCard /></div>
              <div className="faculty-admin-card-info">
                <label>Medical Council Registration</label>
                <span>{faculty.registrationDetails}</span>
              </div>
            </div>
          </div>

          {faculty.additionalInfo && (
            <div className="faculty-admin-modal-section">
              <label>Additional Academic Details / Notes</label>
              <div className="faculty-admin-modal-text-box">{faculty.additionalInfo}</div>
            </div>
          )}

          <div className="faculty-admin-modal-footer-meta">
            <span>Submitted On: <strong>{formatDate(faculty.createdAt)}</strong></span>
          </div>
        </div>

        {/* Modal Footer (Only Close Button) */}
        <div className="faculty-admin-modal-footer">
          <button type="button" className="faculty-admin-modal-btn dismiss" onClick={onClose}>
            Close
          </button>
        </div>

      </div>
    </div>
  );
};

/* =========================================================
   2. DEDICATED PROCESS STATUS MODAL (Opened by '>>' Icon)
========================================================= */
const ProcessStatusModal = ({ faculty, onClose, onUpdateStatus, onDelete }) => {
  if (!faculty) return null;

  return (
    <div className="process-modal-overlay" onClick={onClose}>
      <div className="process-modal-content" onClick={(e) => e.stopPropagation()}>
        
        <div className="process-modal-header">
          <div className="process-modal-icon-badge">
            <FaAngleDoubleRight />
          </div>
          <div>
            <h3>Process Application</h3>
            <p>Candidate: <strong>{faculty.fullName}</strong></p>
          </div>
          <button className="process-modal-close-btn" onClick={onClose} aria-label="Close">
            <FaTimes />
          </button>
        </div>

        <div className="process-modal-body">
          <div className="process-modal-status-indicator">
            <span>Current Status:</span>
            <span className={`faculty-admin-status-badge ${faculty.status.toLowerCase()}`}>
              {faculty.status}
            </span>
          </div>

          <p className="process-modal-instruction">
            Choose the next lifecycle action for this candidate:
          </p>

          {/* Dynamic Stage-wise Action Buttons */}
          <div className="process-modal-btn-stack">
            
            {/* Stage 1: Reviewed -> Shortlist OR Reject */}
            {faculty.status === 'Reviewed' && (
              <>
                <button 
                  type="button" 
                  className="process-action-btn shortlist"
                  onClick={() => {
                    onClose();
                    onUpdateStatus(faculty._id, 'Shortlisted');
                  }}
                >
                  <FaCheck /> Shortlist Candidate
                </button>
                <button 
                  type="button" 
                  className="process-action-btn reject"
                  onClick={() => {
                    onClose();
                    onUpdateStatus(faculty._id, 'Rejected');
                  }}
                >
                  <FaBan /> Reject Application
                </button>
              </>
            )}

            {/* Stage 2: Shortlisted -> Hire OR Reject */}
            {faculty.status === 'Shortlisted' && (
              <>
                <button 
                  type="button" 
                  className="process-action-btn hire"
                  onClick={() => {
                    onClose();
                    onUpdateStatus(faculty._id, 'Hired');
                  }}
                >
                  <FaUserCheck /> Mark Candidate as Hired
                </button>
                <button 
                  type="button" 
                  className="process-action-btn reject"
                  onClick={() => {
                    onClose();
                    onUpdateStatus(faculty._id, 'Rejected');
                  }}
                >
                  <FaBan /> Reject Application
                </button>
              </>
            )}

            {/* Stage 3: Hired -> Reject OR Delete */}
            {faculty.status === 'Hired' && (
              <>
                <button 
                  type="button" 
                  className="process-action-btn reject"
                  onClick={() => {
                    onClose();
                    onUpdateStatus(faculty._id, 'Rejected');
                  }}
                >
                  <FaBan /> Move to Rejected
                </button>
                <button 
                  type="button" 
                  className="process-action-btn delete"
                  onClick={() => {
                    onClose();
                    onDelete(faculty._id, faculty.status);
                  }}
                >
                  <FaTrashAlt /> Delete Profile
                </button>
              </>
            )}

            {/* Stage 4: Rejected -> Re-Shortlist OR Delete */}
            {faculty.status === 'Rejected' && (
              <>
                <button 
                  type="button" 
                  className="process-action-btn shortlist"
                  onClick={() => {
                    onClose();
                    onUpdateStatus(faculty._id, 'Shortlisted');
                  }}
                >
                  <FaCheck /> Re-Shortlist Candidate
                </button>
                <button 
                  type="button" 
                  className="process-action-btn delete"
                  onClick={() => {
                    onClose();
                    onDelete(faculty._id, faculty.status);
                  }}
                >
                  <FaTrashAlt /> Delete Permanently
                </button>
              </>
            )}

            <button type="button" className="process-action-btn cancel" onClick={onClose}>
              Cancel
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

const PAGE_OPTIONS = [10, 20, 30, 40, 50, 100];

/* =========================================================
   3. MAIN FACULTY REGISTRATION ADMIN PAGE
========================================================= */
const FacultyRegistrationForm = () => {
  const [facultyList, setFacultyList] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [expandedRow, setExpandedRow] = useState(null);
  const [viewingFaculty, setViewingFaculty] = useState(null);
  const [processingFaculty, setProcessingFaculty] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const fetchFaculty = async () => {
    try {
      const res = await axiosInstance.get('/api/faculty/all');
      setFacultyList(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      if (error.response?.status !== 401) console.error('Fetch faculty error:', error);
    }
  };

  useEffect(() => {
    fetchFaculty();
  }, []);

  const toggleRow = (id) => setExpandedRow((prev) => (prev === id ? null : id));
  const formatDate = (d) => (d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '-');

  // Filtered List
  const filteredFaculty = useMemo(() => {
    return facultyList.filter((item) => {
      if (statusFilter !== 'All' && item.status !== statusFilter) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const mob = item.mobileNumber || '';
        const spec = item.speciality || '';
        const qual = item.qualification || '';
        const desig = item.currentDesignation || '';
        const city = item.currentCityState || '';
        return (
          item.fullName?.toLowerCase().includes(q) ||
          item.email?.toLowerCase().includes(q) ||
          mob.includes(q) ||
          spec.toLowerCase().includes(q) ||
          qual.toLowerCase().includes(q) ||
          desig.toLowerCase().includes(q) ||
          city.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [facultyList, searchQuery, statusFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter]);

  const paginatedFaculty = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredFaculty.slice(start, start + itemsPerPage);
  }, [filteredFaculty, currentPage, itemsPerPage]);

  // Excel Export
  const handleExportExcel = async () => {
    if (filteredFaculty.length === 0) return Swal.fire('Notice', 'No data available to export.', 'info');

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Faculty Registrations');

    worksheet.mergeCells('A1:L1');
    worksheet.getCell('A1').value = 'NUTRIVIO MEDICAL SERVICES PRIVATE LIMITED';
    worksheet.getCell('A1').font = { name: 'Arial', size: 15, bold: true, color: { argb: 'FF206E88' } };
    worksheet.getCell('A1').alignment = { vertical: 'middle', horizontal: 'center' };

    worksheet.mergeCells('A2:L2');
    worksheet.getCell('A2').value = `Doctor & Faculty Registration Profiles Report (${statusFilter})`;
    worksheet.getCell('A2').font = { name: 'Arial', size: 11, bold: true, color: { argb: 'FF475569' } };
    worksheet.getCell('A2').alignment = { vertical: 'middle', horizontal: 'center' };

    worksheet.addRow([]);

    const headerRow = worksheet.addRow([
      'S.No', 'Full Name', 'Mobile', 'Email Address', 'Qualification', 'Speciality', 'Designation', 
      'Experience', 'Current City/State', 'Preferred Location', 'Status', 'Submitted On'
    ]);

    headerRow.eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF50A5C0' } };
      cell.font = { color: { argb: 'FFFFFFFF' }, bold: true };
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
      cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
    });

    filteredFaculty.forEach((f, i) => {
      const row = worksheet.addRow([
        i + 1,
        f.fullName,
        f.mobileNumber,
        f.email,
        f.qualification,
        f.speciality,
        f.currentDesignation,
        f.teachingExperience,
        f.currentCityState,
        f.preferredLocation,
        f.status,
        formatDate(f.createdAt)
      ]);

      row.eachCell((cell) => {
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
      });
      row.getCell(1).alignment = { horizontal: 'center' };

      const statusCell = row.getCell(11);
      statusCell.alignment = { horizontal: 'center' };
      statusCell.font = { 
        bold: true, 
        color: { 
          argb: f.status === 'Hired' ? 'FF16A34A' : 
                f.status === 'Shortlisted' ? 'FF50A5C0' : 
                f.status === 'Reviewed' ? 'FF2563EB' : 
                f.status === 'Rejected' ? 'FFDC2626' : 'FFE67E22' 
        } 
      };
    });

    worksheet.columns = [
      { width: 8 }, { width: 24 }, { width: 16 }, { width: 28 }, { width: 18 }, { width: 22 }, { width: 22 }, 
      { width: 18 }, { width: 20 }, { width: 20 }, { width: 15 }, { width: 22 }
    ];

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `Nutrivio_Faculty_Registrations_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  // 👉 View Handler (Auto-sets 'Open' to 'Reviewed' on View)
  const handleView = async (facultyItem) => {
    if (facultyItem.status === 'Open') {
      try {
        await axiosInstance.put(`/api/faculty/update/${facultyItem._id}`, { status: 'Reviewed' });
        facultyItem.status = 'Reviewed';
        fetchFaculty();
      } catch (e) {
        console.error('Auto-review failed:', e);
      }
    }
    setViewingFaculty(facultyItem);
  };

  // 👉 Status Updater
  const handleUpdateStatus = async (id, newStatus) => {
    try {
      await axiosInstance.put(`/api/faculty/update/${id}`, { status: newStatus });
      fetchFaculty();
      Swal.fire({
        icon: 'success',
        title: 'Status Updated',
        text: `Candidate status marked as ${newStatus}.`,
        confirmButtonColor: '#50A5C0'
      });
    } catch (error) {
      Swal.fire('Error', 'Failed to update candidate status.', 'error');
    }
  };

  // 👉 Delete Handler (Strictly allowed when Rejected)
  const handleDelete = async (id, status) => {
    if (status !== 'Rejected') {
      return Swal.fire({
        icon: 'error',
        title: 'Action Denied',
        text: 'Only rejected candidate profiles can be deleted.',
        confirmButtonColor: '#50A5C0'
      });
    }

    const res = await Swal.fire({
      title: 'Delete Profile?',
      text: 'This profile and uploaded CV file will be permanently deleted.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#DC2626',
      cancelButtonColor: '#64748B',
      confirmButtonText: 'Yes, Delete',
    });

    if (res.isConfirmed) {
      try {
        await axiosInstance.delete(`/api/faculty/delete/${id}`);
        fetchFaculty();
        Swal.fire('Deleted', 'Profile removed successfully.', 'success');
      } catch (error) {
        Swal.fire('Error', error.response?.data?.message || 'Deletion failed.', 'error');
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>Faculty Registrations | Nutrivio Admin</title>
        <meta name="description" content="View and manage medical faculty registration submissions." />
      </Helmet>

      <div className="faculty-admin-container">
        
        {/* 1. View Detail Modal (No action buttons inside) */}
        <FacultyViewModal
          faculty={viewingFaculty}
          onClose={() => setViewingFaculty(null)}
        />

        {/* 2. Process Status Modal (Opened by '>>' Icon) */}
        <ProcessStatusModal
          faculty={processingFaculty}
          onClose={() => setProcessingFaculty(null)}
          onUpdateStatus={handleUpdateStatus}
          onDelete={handleDelete}
        />

        <h2 className="faculty-admin-title">Medical Faculty Registration Profiles</h2>

        {/* Top Controls */}
        <div className="faculty-admin-controls">
          <div className="faculty-admin-search-wrapper">
            <FaSearch className="faculty-admin-search-icon" />
            <input
              type="text"
              className="faculty-admin-search-input"
              placeholder="Search Candidate, Speciality, City, Phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="faculty-admin-action-group">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="faculty-admin-select"
            >
              <option value="All">All Status</option>
              <option value="Open">Open</option>
              <option value="Reviewed">Reviewed</option>
              <option value="Shortlisted">Shortlisted</option>
              <option value="Hired">Hired</option>
              <option value="Rejected">Rejected</option>
            </select>

            <select
              value={itemsPerPage}
              onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
              className="faculty-admin-select"
            >
              {PAGE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>{opt} / page</option>
              ))}
            </select>

            <button className="faculty-admin-btn export" onClick={handleExportExcel} title="Export to Excel">
              <FaFileExport /> Export Excel
            </button>
          </div>
        </div>

        {/* Table Layout */}
        <div className="faculty-admin-table-wrapper">
          <table className="faculty-admin-table">
            <thead>
              <tr>
                <th className="col-sno">S.No</th>
                <th className="col-name faculty-admin-align-left">Candidate Name</th>
                <th className="col-qual-spec hide-lt-991 faculty-admin-align-left">Qualification & Speciality</th>
                <th className="col-desig hide-lt-1200 faculty-admin-align-left">Designation</th>
                <th className="col-contact hide-lt-768 faculty-admin-align-left">Contact & Email</th>
                <th className="col-exp hide-lt-1200">Experience</th>
                <th className="col-status hide-lt-768">Status</th>
                <th className="col-action">Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedFaculty.length === 0 ? (
                <tr>
                  <td colSpan="8" className="faculty-admin-no-data">
                    No medical faculty registrations found.
                  </td>
                </tr>
              ) : (
                paginatedFaculty.map((f, i) => {
                  const globalIndex = (currentPage - 1) * itemsPerPage + i;
                  const isRowOpen = expandedRow === f._id;

                  return (
                    <React.Fragment key={f._id}>
                      <tr className={`faculty-admin-main-row ${f.status === 'Open' ? 'unread' : ''} ${isRowOpen ? 'row-expanded' : ''}`}>
                        
                        {/* 1. S.No */}
                        <td className="col-sno">{globalIndex + 1}</td>

                        {/* 2. Candidate Name with Toggle Chevron */}
                        <td className="col-name faculty-admin-align-left">
                          <div className="faculty-admin-name-wrapper" onClick={() => toggleRow(f._id)}>
                            <div className="faculty-admin-name-cluster">
                              <span className="faculty-admin-name-strong">{f.fullName}</span>
                              <span className="faculty-admin-city-sub">{f.currentCityState}</span>
                            </div>
                            <button type="button" className="faculty-admin-expand-btn" aria-label="Toggle Details">
                              {isRowOpen ? <FaChevronUp /> : <FaChevronDown />}
                            </button>
                          </div>
                        </td>

                        {/* 3. Qualification & Speciality (> 991px) */}
                        <td className="col-qual-spec hide-lt-991 faculty-admin-align-left">
                          <div className="faculty-admin-qual-cluster">
                            <strong>{f.qualification}</strong>
                            <small>{f.speciality}</small>
                          </div>
                        </td>

                        {/* 4. Designation (> 1200px) */}
                        <td className="col-desig hide-lt-1200 faculty-admin-align-left">
                          <span className="faculty-admin-desig-tag">{f.currentDesignation}</span>
                        </td>

                        {/* 5. Contact & Email (> 768px) */}
                        <td className="col-contact hide-lt-768 faculty-admin-align-left">
                          <div className="faculty-admin-contact-cluster">
                            <strong>{f.mobileNumber}</strong>
                            <small>{f.email}</small>
                          </div>
                        </td>

                        {/* 6. Experience (> 1200px) */}
                        <td className="col-exp hide-lt-1200">
                          <span className="faculty-admin-exp-pill">{f.teachingExperience.split(' ')[0]} Yrs</span>
                        </td>

                        {/* 7. Status (> 768px) */}
                        <td className="col-status hide-lt-768">
                          <span className={`faculty-admin-status-badge ${f.status.toLowerCase()}`}>
                            {f.status}
                          </span>
                        </td>

                        {/* 8. Actions (Starting Open = 1 View icon, Afterwards = View + Process '>>') */}
                        <td className="col-action">
                          <div className="faculty-admin-action-btns">
                            
                            {/* Always View Icon */}
                            <button
                              className="faculty-admin-icon-btn view"
                              title="View Profile Details"
                              onClick={() => handleView(f)}
                            >
                              <FaEye />
                            </button>

                            {/* 👉 Process Action Icon ('>>') Available when NOT 'Open' */}
                            {f.status !== 'Open' && (
                              <button
                                className="faculty-admin-icon-btn process"
                                title="Process Application (>>)"
                                onClick={() => setProcessingFaculty(f)}
                              >
                                <FaAngleDoubleRight />
                              </button>
                            )}

                          </div>
                        </td>
                      </tr>

                      {/* Expanded Details Row */}
                      {isRowOpen && (
                        <tr className="faculty-admin-expanded-row">
                          <td colSpan="8">
                            <div className="faculty-admin-expanded-content">
                              
                              {/* Status (Shows on < 768px) */}
                              <div className="faculty-admin-exp-item show-exp-lt-768">
                                <span className="faculty-admin-exp-label">Status</span>
                                <div>
                                  <span className={`faculty-admin-status-badge ${f.status.toLowerCase()}`}>
                                    {f.status}
                                  </span>
                                </div>
                              </div>

                              {/* Contact & Email (Shows on < 768px) */}
                              <div className="faculty-admin-exp-item show-exp-lt-768">
                                <span className="faculty-admin-exp-label">Contact Details</span>
                                <span className="faculty-admin-exp-value">{f.mobileNumber} | {f.email}</span>
                              </div>

                              {/* Qualification & Speciality (Shows on < 991px) */}
                              <div className="faculty-admin-exp-item show-exp-lt-991">
                                <span className="faculty-admin-exp-label">Qualification & Speciality</span>
                                <span className="faculty-admin-exp-value">{f.qualification} ({f.speciality})</span>
                              </div>

                              {/* Designation (Shows on < 1200px) */}
                              <div className="faculty-admin-exp-item show-exp-lt-1200">
                                <span className="faculty-admin-exp-label">Current Designation</span>
                                <span className="faculty-admin-exp-value">{f.currentDesignation}</span>
                              </div>

                              {/* Experience (Shows on < 1200px) */}
                              <div className="faculty-admin-exp-item show-exp-lt-1200">
                                <span className="faculty-admin-exp-label">Teaching Experience</span>
                                <span className="faculty-admin-exp-value">{f.teachingExperience}</span>
                              </div>

                              {/* Medical Council Reg */}
                              <div className="faculty-admin-exp-item">
                                <span className="faculty-admin-exp-label">Registration Details</span>
                                <span className="faculty-admin-exp-value">{f.registrationDetails}</span>
                              </div>

                              {/* Preferred Location */}
                              <div className="faculty-admin-exp-item">
                                <span className="faculty-admin-exp-label">Preferred Location</span>
                                <span className="faculty-admin-exp-value">{f.preferredLocation}</span>
                              </div>

                              {/* Joining Availability */}
                              <div className="faculty-admin-exp-item">
                                <span className="faculty-admin-exp-label">Availability</span>
                                <span className="faculty-admin-exp-value">{f.joiningAvailability}</span>
                              </div>

                              {/* CV File Link */}
                              <div className="faculty-admin-exp-item">
                                <span className="faculty-admin-exp-label">Attached CV</span>
                                <a 
                                  href={`http://localhost:5000/uploads/cv/${f.cvFile}`} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="faculty-admin-cv-link"
                                >
                                  <FaFilePdf /> View / Download CV ({f.cvOriginalName || 'Document'})
                                </a>
                              </div>

                              {/* Additional Details */}
                              {f.additionalInfo && (
                                <div className="faculty-admin-exp-item faculty-admin-col-span-full">
                                  <span className="faculty-admin-exp-label">Additional Academic Details / Notes</span>
                                  <div className="faculty-admin-text-content">{f.additionalInfo}</div>
                                </div>
                              )}

                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {filteredFaculty.length > 0 && (
          <Pagination
            totalItems={filteredFaculty.length}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
          />
        )}

      </div>
    </>
  );
};

export default FacultyRegistrationForm;